'use client';

import { useEffect, useState, type ComponentType } from 'react';

import { shortDate } from '@/components/platform/format';
import { cityCoordinates } from '@/lib/platform/cities';
import { approachingCap, type PlatformTenantRow } from '@/lib/platform/summary';
import { usePlatformSummary } from '@/pages/platform/usePlatformSummary';

const COUNT_LABELS = [
  ['agencies', 'Agencies'],
  ['active', 'Active'],
  ['paused', 'Paused'],
  ['unreachable', 'Unreachable'],
  ['nearCap', 'Near a cap'],
] as const;

export function OverviewPage() {
  const { overview, error, reload } = usePlatformSummary('Could not load the platform overview.');

  return (
    <section className="space-y-4">
      <p className="text-sm text-muted">Counts across every agency. Customer names, plates, and booking rows stay in that agency’s database.</p>
      {error && (
        <div className="rounded-2xl border border-danger/40 bg-card px-4 py-6">
          <p className="text-sm text-danger">{error}</p>
          <button type="button" onClick={reload} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
            Try again
          </button>
        </div>
      )}
      {overview === null && !error && <OverviewSkeleton />}
      {overview?.tenants.length === 0 && (
        <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No agencies are registered yet.</p>
      )}
      {overview && overview.tenants.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {COUNT_LABELS.map(([key, label]) => (
              <div key={key} className="rounded-2xl border border-line bg-card px-4 py-3">
                <p className="text-xs font-medium text-muted">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-ink">{overview.counts[key]}</p>
              </div>
            ))}
          </div>
          <AgencyPlaces tenants={overview.tenants} />
          <div className="overflow-x-auto rounded-2xl border border-line bg-card">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Agency</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Vehicles</th>
                  <th className="px-4 py-3 font-medium">Staff</th>
                  <th className="px-4 py-3 font-medium">Bookings</th>
                  <th className="px-4 py-3 font-medium">Open</th>
                  <th className="px-4 py-3 font-medium">Booked</th>
                  <th className="px-4 py-3 font-medium">Storefront</th>
                </tr>
              </thead>
              <tbody>
                {overview.tenants.map((tenant) => (
                  <TenantRow key={tenant.slug} tenant={tenant} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function AgencyPlaces({ tenants }: { tenants: PlatformTenantRow[] }) {
  const pins = tenants.flatMap((tenant) => {
    const known = cityCoordinates(tenant.city);
    const point = tenant.latitude !== null && tenant.longitude !== null
      ? { latitude: tenant.latitude, longitude: tenant.longitude }
      : known === null
        ? null
        : { latitude: known.lat, longitude: known.lng };
    if (point === null) return [];
    return [{ id: tenant.slug, name: tenant.name, city: tenant.city, ...point }];
  });
  const [MapCanvas, setMapCanvas] = useState<ComponentType<{ pins: typeof pins }> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('@/components/maps/LocationMap').then((module) => {
      if (!cancelled) setMapCanvas(() => module.PlacePinsMap);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (pins.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-line">
      {MapCanvas ? <MapCanvas pins={pins} /> : <div className="h-[420px] bg-card" />}
    </section>
  );
}

function TenantRow({ tenant }: { tenant: PlatformTenantRow }) {
  return (
    <tr className="border-t border-line align-top">
      <td className="px-4 py-3">
        <p className="font-medium text-ink">{tenant.name}</p>
        <p className="text-xs text-muted">{tenant.city} · {tenant.slug}</p>
        {tenant.error && <p className="mt-1 text-xs text-danger">{tenant.error}</p>}
      </td>
      <td className="px-4 py-3 capitalize text-ink">{tenant.status}</td>
      <td className="px-4 py-3 text-ink">
        <UsageCell used={tenant.vehicles} limit={tenant.vehicleLimit} nearCap={approachingCap(tenant.vehicleLimit, tenant.vehicles)} />
      </td>
      <td className="px-4 py-3 text-ink">
        <UsageCell used={tenant.staffAccounts} limit={tenant.staffLimit} nearCap={approachingCap(tenant.staffLimit, tenant.staffAccounts)} />
      </td>
      <td className="px-4 py-3 text-ink">{count(tenant.bookings)}</td>
      <td className="px-4 py-3 text-ink">{count(tenant.openBookings)}</td>
      <td className="px-4 py-3 text-ink">{tenant.bookedValue === null ? '—' : `${tenant.bookedValue} DH`}</td>
      <td className="px-4 py-3 text-ink">{tenant.storefrontPublishedAt ? shortDate(tenant.storefrontPublishedAt) : tenant.error ? '—' : 'Not published'}</td>
    </tr>
  );
}

function UsageCell({ used, limit, nearCap }: { used: number | null; limit: number | null; nearCap: boolean }) {
  if (used === null) return '—';
  const label = limit === null ? String(used) : `${used} / ${limit}`;
  return <span className={nearCap ? 'font-semibold text-warning' : undefined}>{label}</span>;
}

function count(value: number | null): string {
  return value === null ? '—' : String(value);
}

function OverviewSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      <div className="grid gap-3 sm:grid-cols-5">
        {COUNT_LABELS.map(([key]) => (
          <div key={key} className="h-16 animate-pulse rounded-2xl bg-card" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl bg-card" />
    </div>
  );
}
